import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { getDjangoEndpoint } from "src/utils/apiEndpoints";
import { toApiError } from "src/types/api";
import axiosInstance from "src/store/features/auth/axiosCSRF";
interface FileInfo {
  file_name: string;
  file_extension: string;
  file_size: number | string;
  s3_path?: string;
}

const INVALID_KINASE_TARGET_KEYS = new Set([
  "file_name",
  "file_extension",
  "file_size",
  "s3_path",
]);

const isEmptyKinaseTargetName = (value?: string | null) => !value?.trim();

const isFileInfoMetadataKey = (value: string) =>
  INVALID_KINASE_TARGET_KEYS.has(value);

const isFileInfoLike = (value: any) =>
  value &&
  typeof value === "object" &&
  ("file_name" in value || "file_extension" in value || "file_size" in value);

const getTargetFileInfoEntries = (
  fileInfo: any,
  fallbackTargetName?: string | null
): [string, any][] => {
  if (!fileInfo || typeof fileInfo !== "object") {
    return [];
  }

  if (isFileInfoLike(fileInfo)) {
    return isEmptyKinaseTargetName(fallbackTargetName)
      ? []
      : [[fallbackTargetName as string, fileInfo]];
  }

  return Object.entries(fileInfo).filter(
    ([targetName]) =>
      !isEmptyKinaseTargetName(targetName) && !isFileInfoMetadataKey(targetName)
  );
};

const getKinaseFileInfoFromFileName = (fileName?: string | null): FileInfo | null => {
  if (!fileName) {
    return null;
  }

  const cleanFileName = fileName.replace(/\.(pdb|cif)$/i, "");
  const fileExt = fileName.match(/\.(pdb|cif)$/i)?.[1] || "pdb";

  return {
    file_name: cleanFileName,
    file_extension: fileExt,
    file_size: 0,
  };
};

const getAssayFileInfoFromResponse = (response: any): FileInfo | null => {
  if (response?.assay_file_info) {
    return response.assay_file_info;
  }

  const fileName = response?.assay_file_name;
  if (!fileName) {
    return null;
  }

  const fileExt =
    response?.assay_file_ext ||
    response?.assay_file_extension ||
    response?.assay_file_type ||
    String(fileName).match(/\.([^.]+)$/)?.[1] ||
    "csv";

  return {
    file_name: fileName,
    file_extension: String(fileExt).replace(/^\./, ""),
    file_size: response?.assay_file_size ?? 0,
    s3_path: response?.assay_s3_path,
  };
};

const normalizeKinaseCoordinates = (coords: any): string | null => {
  if (!coords) {
    return null;
  }

  if (Array.isArray(coords)) {
    return coords.join(",");
  }

  if (typeof coords === "string") {
    const normalizedCoords = coords.replace(/\s/g, "");
    return normalizedCoords || null;
  }

  return null;
};

const getTargetFileInfoFromResponse = (
  response: any,
  targetName?: string | null,
  kinaseData?: any
): FileInfo | null => {
  const targetFileEntries = getTargetFileInfoEntries(
    response?.target_file_info,
    targetName
  );
  const kinaseTargetEntries = getTargetFileInfoEntries(
    response?.kinase_target_info,
    targetName
  );
  const targetFileInfo =
    targetFileEntries.find(([name]) => name === targetName)?.[1] ||
    kinaseTargetEntries.find(([name]) => name === targetName)?.[1] ||
    targetFileEntries[0]?.[1] ||
    kinaseTargetEntries[0]?.[1] ||
    getKinaseFileInfoFromFileName(kinaseData?.file);

  return targetFileInfo || null;
};

const syncSelectedTargetFileInfo = (
  state: any,
  targetName?: string | null,
  fileInfo?: FileInfo | null
) => {
  if (!targetName || !fileInfo) {
    return;
  }

  state.target_file_info = fileInfo;
  state.userUploadTargetPdbFileInfo = fileInfo;
  state.userUploadTargetPdbName = targetName;
};

const syncSelectedTargetDockingInfo = (
  state: any,
  kinaseData: any,
  shouldUseChainLigand: boolean
) => {
  if (shouldUseChainLigand) {
    if (kinaseData?.chain) {
      state.userUploadTargetPdbChain = kinaseData.chain;
      state.target_chain = kinaseData.chain;
      state.chain = kinaseData.chain;
    }
    if (kinaseData?.ligand_id) {
      state.userUploadTargetPdbLigandId = kinaseData.ligand_id;
      state.userUploadTargetPdbLigandFullName = kinaseData.ligand_id;
      state.target_ligand_id = kinaseData.ligand_id;
      state.ligand_id = kinaseData.ligand_id;
    }
    state.userUploadLocationXYZ = null;
    state.locationXYZ = null;
    state.userUploadChoiceDockingBoxTab = null;
    state.userChoiceDockingBoxTab = null;
    return;
  }

  state.userUploadTargetPdbChain = null;
  state.userUploadTargetPdbLigandId = null;
  state.userUploadTargetPdbLigandFullName = null;
  state.target_chain = null;
  state.target_ligand_id = null;
  state.chain = null;
  state.ligand_id = null;

  const coordinatesStr = Array.isArray(kinaseData?.coordinates)
    ? kinaseData.coordinates.join(",")
    : kinaseData?.coordinates || null;

  state.userUploadLocationXYZ = coordinatesStr;
  state.locationXYZ = coordinatesStr;
  state.userUploadChoiceDockingBoxTab = "coordinates";
  state.userChoiceDockingBoxTab = "coordinates";
};

// 임시 저장 Filter Callback 에러 타입
interface ApiError {
  status?: number;
  code?: string;
  message: string;
  details?: any;
}

// Chain과 Ligand 인터페이스 추가
interface Chain {
  id: string;
  name: string;
}

interface Ligand {
  id: string;
  name: string;
  resName: string;
  chainId: string;
}

//  Off-target 파일 정보 타입 - chain/ligand 정보 추가
interface OffTargetFileInfo {
  file: File | null;
  file_type: string | null;
  file_info: FileInfo | null;
  upload_status?: 'idle' | 'uploading' | 'success' | 'error';
  error_message?: string | null;
  // 추가: chain/ligand 정보
  chains?: Chain[];
  ligands?: Ligand[];
  selectedChain?: string | null;
  selectedLigand?: string | null;
  pdbContent?: string | null; // ← 추가
}

// Off-target 데이터 타입
interface OffTargetData {
  id?: number;
  name: string;
  proteinName?: string;
  proteinSeq?: string;
  seq?: string;
  geneID ?: string;
  chain?: string;
  ligandId?: string;
  ligand_id?: string;
  file?: string;
  coordinates?: string;
  userChoiceLocationXYZ?: string;
  dockingBoxTab?: string;
  [key: string]: any;
}

// Target 정보 타입
interface TargetInfo {
  proteinName?: string;
  proteinSeq?: string;
  chain?: string;
  ligandId?: string;
  ligand_id?: string;
  coordinates?: string;
  [key: string]: any;
}

// Assay 정보 타입
interface AssayInfo {
  assay_id_col: string | null;
  smiles_col: string | null;
  activity_col: string | null;
  activity_col_rename: string | null;
  threshold_val: string | number | null;
  threshold_direction: string | null;
  active_vals: string;
  inactive_vals: string;
}

// workFlowFilterCallbackAPI 파라미터 타입 정의
interface WorkFlowFilterCallbackParams {
  sample_use?: boolean;
  library?: string | null;
  target_file_info?: FileInfo | null;
  drug_file_info?: FileInfo | null;
  assay_file_info?: FileInfo | null;
  filter_adme_threshold?: number | null;
  filter_druglikeness?: any;
  filter_plip?: any;
  protein_name?: string | null;
  protein_seq?: string | null;
  chain?: string | null;
  ligand_id?: string | null;
  assay_id?: string | null;
  gt_label?: string | null;
  assay_smiles_col?: string | null;
  num_mol?: number | null;
  [key: string]: any;
}

// WorkFlowFileCallBackAPI 파라미터 타입 정의
export interface WorkflowGetCallBackFileList {
  group_name: string;
  library_name: string;
  round: number;
  workflow_name?: string;
  file_type?: string;
  sample_use?: boolean ;
  saveToTargetPdbSave?: boolean;
  saveToMimicPdbSave?: boolean;
  saveToAssayCsvSave?: boolean;
  target_name?: string;
  off_target_name?: string;
}

// 통합된 iUserParameterState 인터페이스
export interface iKinaseUserParameterState {
  library?: string | null;
  user_upload_target_file : File | null;
  target_file: File | null;
  target_file_type: string | null;
  target_file_info: FileInfo | null;
  assay_file: File | null;
  assay_file_type: string | null;
  assay_file_info: FileInfo | null;
  assay_id: string | null;
  gt_label: string | null;
  filter_druglikeness: {
    drug_filter: {
      [key: string]: any;
    };
    min_hba_hbd_sum: number | null;
    qeppi_threshold: number | null;
    sa_threshold: number | null;
  };
  filter_kinase: {
    apply_bioactivity_filter: boolean;
    bioactivity_types: string[];
    exclude_metal: boolean;
    apply_structural_filter: boolean;
    structural_filters: {
      [key: string]: [number | null, number | null];
    };
  };
  filter_plip: {
    [key: string]: any;
  };
  hasPlipData: boolean;
  filter_adme_threshold: number | null;
  molecularNumber: number | null;
  target_chain: string | null;
  target_ligand_id: string | null;
  drug_receptor_chain_id: string | null;
  drug_chain_ligand_id: string | null;
  protein_name: string | null;
  protein_seq: string | null;
  target_all_residue: string[];
  selected_plip_residues: string[];
  userChoiceStep: number;
  locationXYZ: string | null;
  chain: string | null;
  ligand_id: string | null;
  userChoiceDockingBoxTab: string | null;

  userFileChecking: string | null;
  userInputWorkSpaceName: string;
  userChoiceTool?: string;
  userChoiceKinaseTarget: string;
  userChoiceKinaseOffTargetList?: string;
  userToolRunningStatus: {
    [toolName: string]: {
      isRunning: boolean;
      error?: string;
    };
  };
  userHaveAssayFile: boolean;
  userDontKnowResidueState: boolean;
  
  // workFlowFileCallback에서 추가된 상태들
  response: any;
  TargetPdbResponse: any;
  round2Enabled: boolean;
  round2EnabledLibraries: any;
  AssayCsvResponse: any;
  isFileCallbackLoading: boolean;
  isFilterCallbackLoading: boolean;
  message: string | null;
  status: number | null;
  // 개별 로딩 상태
  isTargetLoading: boolean;
  isMimicLoading: boolean;
  isAssayLoading: boolean;
  error: string | null;
  showNextRoundAssayModal : boolean,
  userRunningStep : number,
  autoStep:string | null;
  cardExpanded : boolean;
  setFullNameTargetLigandId : string | null;

  userChoiceTargetName : string | null;
  userChoiceOffTargetName : {
    id : number,
    name : string,
    distance : any
  };
  // Target Off Target Reducer
  targetOffTargetData: {
    [targetName: string]: {
      targetInfo?: TargetInfo;
      offTargets: {
        [offTargetName: string]: OffTargetData;
      };
      offTargetFiles: {
        [offTargetName: string]: OffTargetFileInfo; // ← 여기에 각 off-target PDB 저장
      };
    };
  }
  kinase_off_targets : string[]
  userUploadTargetPdbResponse : any;
  userUploadTargetPdbName : string;
  userUploadTargetPdbChain : string | null;
  userUploadTargetPdbLigandId : string | null;
  userUploadTargetPdbLigandFullName : string | null;
  userUploadTargetPdbFileType : string | null;
  userUploadTargetPdbFileInfo : FileInfo | null;
  userUploadChoiceDockingBoxTab : string | null;
  userUploadLocationXYZ : string;
  
  // 추가: 통합된 chain/ligand 관리
  availableChains: Chain[];
  availableLigands: Ligand[];
  
  // Assay 정보를 객체로 통합
  assay_info: AssayInfo;
  isLoadedFromTemporarySave: boolean, // 추가
  // 추가: sample data 사용 여부 추적
  isSampleData: boolean;
  sample_use : boolean;
  userChoiceKinaseResultTaskName : string;
  apiError: ApiError | null;
  docking_smiles : string | null;
}

// 초기 상태
const emptyAssayInfo = {
  assay_id_col: null,
  smiles_col: null,
  activity_col: null,
  activity_col_rename: null,
  threshold_val: null,
  threshold_direction: null,
  active_vals: "",
  inactive_vals: ""
};

const isEmptyCallbackData = (data: any) => {
  if (data == null) return true;
  if (typeof data === "string") return data.trim().length === 0;
  if (Array.isArray(data)) return data.length === 0;
  if (typeof data === "object") return Object.keys(data).length === 0;
  return false;
};

const clearAssayState = (state: iKinaseUserParameterState) => {
  state.AssayCsvResponse = null;
  state.assay_file = null;
  state.assay_file_type = null;
  state.assay_file_info = null;
  state.userHaveAssayFile = false;
  state.assay_id = null;
  state.gt_label = null;
  state.assay_info = { ...emptyAssayInfo };
  state.isAssayLoading = false;
};

const initialState: iKinaseUserParameterState = {
  library: null,
  target_file: null,
  target_file_type: null,
  target_file_info: null,
  assay_file: null,
  assay_file_type: null,
  assay_file_info: null,
  assay_id: null,
  gt_label: null,
  filter_druglikeness: {
    drug_filter: {},
    min_hba_hbd_sum: null,
    qeppi_threshold: null,
    sa_threshold: null,
  },
  filter_kinase: {
    apply_bioactivity_filter: false,
    bioactivity_types: ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
    exclude_metal: false,
    apply_structural_filter: false,
    structural_filters: {
      "MW": [null, null],
      "HBA": [null, null],
      "HBD": [null, null],
      "NUM_AROMRINGS": [null, null]
    }
  },
  filter_plip: {
    plip: {}
  },
  hasPlipData: false,
  filter_adme_threshold: null,
  molecularNumber: null,
  target_chain: null,
  target_ligand_id: null,
  drug_receptor_chain_id: null,
  drug_chain_ligand_id: null,
  protein_name: null,
  protein_seq: null,
  target_all_residue: [],
  selected_plip_residues: [],
  userChoiceStep: 10,
  locationXYZ: null,
  chain: null,
  ligand_id: null,
  userChoiceDockingBoxTab: null,
  userFileChecking: null,
  userInputWorkSpaceName: "",
  userChoiceTool: null,
  userChoiceKinaseTarget: "",
  userChoiceKinaseOffTargetList: null,
  userToolRunningStatus: {},
  userHaveAssayFile: false,
  userDontKnowResidueState: false,
  round2Enabled: false,
  round2EnabledLibraries: {},
  response: null,
  TargetPdbResponse:null,
  AssayCsvResponse: null,
  isFileCallbackLoading: false,
  isFilterCallbackLoading: false,
  message: "",
  status: null,
  isTargetLoading: false,
  isMimicLoading: false,
  isAssayLoading: false,
  error: null,
  showNextRoundAssayModal :false,
  userRunningStep : null,
  autoStep : null,
  cardExpanded : false,
  setFullNameTargetLigandId : null,
  userChoiceTargetName : null,
  userChoiceOffTargetName : null,
  targetOffTargetData: {},
  userUploadTargetPdbResponse : null,
  userUploadTargetPdbName : null,
  userUploadTargetPdbChain : null,
  userUploadTargetPdbLigandId : null,
  userUploadTargetPdbLigandFullName : null,
  userUploadTargetPdbFileType : null,
  userUploadTargetPdbFileInfo : null,
  user_upload_target_file : null,
  userUploadChoiceDockingBoxTab : null,
  userUploadLocationXYZ : null,
  kinase_off_targets : [],
  
  // 추가
  availableChains: [],
  availableLigands: [],
  isSampleData: false,
  
  assay_info: {
    assay_id_col: null,
    smiles_col: null,
    activity_col: null,
    activity_col_rename: null,
    threshold_val: null,
    threshold_direction: null,
    active_vals: "",
    inactive_vals: ""
  },
  sample_use : false,
  isLoadedFromTemporarySave: false,
  userChoiceKinaseResultTaskName : null,
  apiError: null,
  docking_smiles : null,

};

// 임시저장 Filter Callback 
export const temporarySaveFilterCallbackAPI = createAsyncThunk(
  "userParameter/temporarySaveFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/temp-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const requestBody = Object.keys(params).length > 0 ? params : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const temporarySaveFileCallBackAPI = createAsyncThunk(
  "userParameter/temporarySaveFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = '/workflow/temp-file-callback/';
      
      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      // sample_use 명확하게 전달
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }
      
      if (data.target_name) {
        requestData.target_name = data.target_name;
      }
      
      if (data.off_target_name) {
        requestData.off_target_name = data.off_target_name;
      }
      
      
      const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestData);
      
      return {
        sample_use: Boolean(data.sample_use ), // 요청한 값 그대로 반환
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('❌ temporarySaveFileCallBackAPI Error:', error);
      return rejectWithValue(error);
    }
  }
);


// workFlowFilterCallbackAPI
export const workFlowFilterCallbackAPI = createAsyncThunk(
  "userParameter/workFlowFilterCallback",
  async (params: WorkFlowFilterCallbackParams = {}, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = "/workflow/filter-callback/";

      if (!djangoEndpoint) {
        throw new Error("Django endpoint not found");
      }

      const { restore_assay_info: _restoreAssayInfo, ...apiParams } = params;
      const requestBody = Object.keys(apiParams).length > 0 ? apiParams : {};

      const response = await axiosInstance.post(
        `${djangoEndpoint}${pathParam}`,
        requestBody,
        { withCredentials: true }
      );

      return response.data;
    } catch (error) {
      const apiError = toApiError(error);
      return rejectWithValue(apiError.response?.data || apiError.message);
    }
  }
);

export const WorkFlowFileCallBackAPI = createAsyncThunk(
  "userParameter/WorkFlowFileCallBack",
  async (data: WorkflowGetCallBackFileList, { rejectWithValue }) => {
    try {
      const djangoEndpoint = getDjangoEndpoint();
      const pathParam = '/workflow/file-callback/';
      
      const requestData: any = {
        group_name: data.group_name,
        library_name: data.library_name,
        workflow_name: data.workflow_name,
        round: data.round,
        file_type: data.file_type,
      };

      // sample_use 명확하게 전달
      if (data.sample_use !== undefined) {
        requestData.sample_use = data.sample_use;
      }
      
      if (data.target_name) {
        requestData.target_name = data.target_name;
      }
      
      if (data.off_target_name) {
        requestData.off_target_name = data.off_target_name;
      }
      
      
      const response = await axiosInstance.post(`${djangoEndpoint}${pathParam}`, requestData);
      
      return {
        sample_use: Boolean(data.sample_use ), // 요청한 값 그대로 반환
        saveToTargetPdbResponse: Boolean(data.saveToTargetPdbSave || false),
        saveToMimicPdbResponse: Boolean(data.saveToMimicPdbSave || false),
        saveToAssayCsvResponse: Boolean(data.saveToAssayCsvSave || false),
        data: response.data,
        status: response.status
      };
    } catch (error) {
      console.error('❌ WorkFlowFileCallBackAPI Error:', error);
      return rejectWithValue(error);
    }
  }
);

// UserParameter Slice
export const userKinaseParameterSlice = createSlice({
  name: "userKinaseParameter",
  initialState,
  reducers: {
    setTargetFile: (state, action) => {
      state.target_file = action.payload;
    },
    setUserTargetFile: (state, action) => {
      state.user_upload_target_file = action.payload;
      // 사용자가 새 파일 업로드하면 sample data 플래그 해제
      if (action.payload) {
        state.isSampleData = false;
        state.sample_use = false; 
      }
    },
    setTargetFileType: (state, action) => {
      state.target_file_type = action.payload;
    },
    setAssayFile: (state, action) => {
      state.assay_file = action.payload;
    },
    setAssayFileType: (state, action) => {
      state.assay_file_type = action.payload;
    },
    setAssayId: (state, action) => {
      state.assay_id = action.payload;
    },
    setGtLabel: (state, action) => {
      state.gt_label = action.payload;
    },
    setAutoStepSave: (state, action) => {
      state.autoStep = action.payload;
    },
    setDruglikeness: (state, action) => {
      if (action.payload) {
        if (action.payload.drug_filter) {
          state.filter_druglikeness = {
            ...state.filter_druglikeness,
            drug_filter: { ...action.payload.drug_filter },
          };
        }

        if (action.payload.min_hba_hbd_sum !== undefined) {
          state.filter_druglikeness.min_hba_hbd_sum =
            action.payload.min_hba_hbd_sum;
        }

        if (action.payload.qeppi_threshold !== undefined) {
          state.filter_druglikeness.qeppi_threshold =
            action.payload.qeppi_threshold;
        }

        if (action.payload.sa_threshold !== undefined) {
          state.filter_druglikeness.sa_threshold = action.payload.sa_threshold;
        }
      }
    },
    setBioactivityFilter: (state, action) => {
      state.filter_kinase = action.payload;
    },
    setPlip: (state, action) => {
      state.filter_plip = action.payload;

      const selectedResidues: string[] = [];

      if (action.payload && action.payload.plip) {
        Object.entries(action.payload.plip).forEach(
          ([interactionType, params]) => {
            if (
              params &&
              typeof params === "object" &&
              "residues" in params &&
              params.residues
            ) {
              selectedResidues.push(params.residues as string);
            }
          }
        );
      }

      state.selected_plip_residues = selectedResidues;
    },
    clearPlipData: (state) => {
      state.filter_plip = { plip: {} };
      state.hasPlipData = false;
      state.selected_plip_residues = [];
    },
    setAdme: (state, action) => {
      state.filter_adme_threshold = action.payload;
    },
    setMolecularNumber: (state, action) => {
      state.molecularNumber = action.payload;
    },
    setTargetChainID: (state, action) => {
      state.target_chain = action.payload;
    },
    setTargetLigandID: (state, action) => {
      state.target_ligand_id = action.payload;
    },
    setDrugReceptorChainID: (state, action) => {
      state.drug_receptor_chain_id = action.payload;
    },
    setDrugChainLigandID: (state, action) => {
      state.drug_chain_ligand_id = action.payload;
    },
    setLocationXYZ: (state, action) => {
      state.locationXYZ = action.payload;
    },
    setProteinName: (state, action) => {
      state.protein_name = action.payload;
    },
    setProteinSequence: (state, action) => {
      state.protein_seq = action.payload;
    },
    setAllResidueList: (state, action) => {
      state.target_all_residue = action.payload;
    },
    setSelectedPlipResidues: (state, action) => {
      state.selected_plip_residues = action.payload;
    },
    setUserChoiceStep: (state, action) => {
      state.userChoiceStep = action.payload;
    },
    setUserChain: (state, action) => {
      state.chain = action.payload;
    },
    setUserLigandID: (state, action) => {
      state.ligand_id = action.payload;
    },
    setUserChoiceDockingTab: (state, action) => {
      state.userChoiceDockingBoxTab = action.payload;
    },
    setUserFileUploadChecking: (state, action) => {
      state.userFileChecking = action.payload;
    },
    setUserRunningStep: (state, action) => {
      state.userRunningStep = action.payload;
    },
    setIsLoadedFromTemporarySave: (state, action: PayloadAction<boolean>) => {
      state.isLoadedFromTemporarySave = action.payload;
    },
    
    resetAllParametersExceptLibrary: (state, action) => {
      const libraryName = action.payload;

      Object.assign(state, {
        ...initialState,
        library: libraryName,
      });
    },
    resetAllParameters: (state) => {
      const currentLibrary = state.library;
      Object.assign(state, {
        ...initialState,
        library: currentLibrary,
      });
    },
    resetAllFileInfo: (state) => {
      state.target_file = null;
      state.assay_file = null;
      state.target_file_type = null;
      state.assay_file_type = null;
      state.target_file_info = null;
      state.assay_file_info = null;
      state.userFileChecking = null;
      state.userHaveAssayFile = false;
    },
    setUserChoiceTool: (state, action) => {
      state.userChoiceTool = action.payload;
    },
    // reducer에 추가
setUserInputWorkSpaceName: (state, action) => {
  state.userInputWorkSpaceName = action.payload;
  // Workspace 변경 시 sample_use 초기화
  if (action.payload !== state.userInputWorkSpaceName) {
    state.isSampleData = false;
  }
},
    setUserChoiceKinaseTarget: (state, action) => {
      state.userChoiceKinaseTarget = action.payload;
    },
    setCardExpanded: (state, action) => {
      state.cardExpanded = action.payload;
    },
    setUserChoiceKinaseOffTargetList: (state, action) => {
      state.userChoiceKinaseOffTargetList = action.payload;
    },
    setUserToolRunningStatus: (state, action) => {
      const { libraryName, isRunning, error } = action.payload;
      state.userToolRunningStatus[libraryName] = { isRunning, error };
    },
    setUserHaveAssayFile: (state, action) => {
      state.userHaveAssayFile = action.payload;
    },
    setTargetFileInfo: (state, action) => {
      state.target_file_info = action.payload;
    },
    setAssayFileInfo: (state, action) => {
      state.assay_file_info = action.payload;
    },

    setPdbLigandResponse: (state, action: PayloadAction<any>) => {
      state.TargetPdbResponse = action.payload;
      state.AssayCsvResponse = action.payload;
      state.userUploadTargetPdbResponse = action.payload;
    },
    setTargetPdbResponse: (state, action: PayloadAction<any>) => {
      state.TargetPdbResponse = action.payload;
    },

    setAssayCsvResponse: (state, action: PayloadAction<any>) => {
      state.AssayCsvResponse = action.payload;
    },
    clearAssayCallbackState: (state) => {
      clearAssayState(state);
    },
    resetWorkflowResult: (state) => {
      state.response = null;
      state.status = null;
      state.message = "";
      state.isFileCallbackLoading = false;
      state.isFilterCallbackLoading = false;
    },
    resetWorkFlowFileCallback: (state) => {
      state.response = null;
      state.userUploadTargetPdbResponse = null;
      state.TargetPdbResponse = null;
      state.AssayCsvResponse = null;
      state.target_file = null;
      state.target_file_type = null;
      state.target_file_info = null;
      state.assay_file = null;
      state.assay_file_type = null;
      state.assay_file_info = null;
      state.userUploadTargetPdbFileInfo = null;
      state.isFileCallbackLoading = false;
      state.isFilterCallbackLoading = false;
      state.message = "";
      state.status = null;
      state.isTargetLoading = false;
      state.isMimicLoading = false;
      state.isAssayLoading = false;
    },
    setUserChoiceTargetName: (state, action: PayloadAction<string>) => {
      state.userChoiceTargetName = action.payload;
      state.assay_info.activity_col_rename = action.payload;
    },
    setUserChoiceOffTargetName: (
      state,
      action: PayloadAction<{ id: number; name: string; distance: number }>
    ) => {
      state.userChoiceOffTargetName = action.payload;
    },
    setTargetInfo: (
      state,
      action: PayloadAction<{ targetName: string; targetInfo: TargetInfo }>
    ) => {
      const { targetName, targetInfo } = action.payload;

      if (!state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName] = {
          offTargets: {},
          offTargetFiles: {},
        };
      }

      state.targetOffTargetData[targetName].targetInfo = targetInfo;
    },

    addOffTarget: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        offTargetData: Partial<OffTargetData>;
      }>
    ) => {
      const { targetName, offTargetName, offTargetData } = action.payload;

      if (!state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName] = {
          offTargets: {},
          offTargetFiles: {},
        };
      }

      if (!state.targetOffTargetData[targetName].offTargets[offTargetName]) {
        state.targetOffTargetData[targetName].offTargets[offTargetName] = {
          name: offTargetName,
          chain: null,
          ligandId: null,
          ligand_id: null,
          seq: null,
          file: null,
          coordinates: null,
          userChoiceLocationXYZ: null,
          dockingBoxTab: null,
          ...offTargetData,
        };
      } else {
        state.targetOffTargetData[targetName].offTargets[offTargetName] = {
          ...state.targetOffTargetData[targetName].offTargets[offTargetName],
          ...offTargetData,
        };
      }
    },

    updateOffTargetData: (state, action: PayloadAction<{
      targetName: string;
      offTargetName: string;
      updates: Partial<OffTargetData>;
    }>) => {
      const { targetName, offTargetName, updates } = action.payload;

      if (!targetName || targetName.trim() === "") {
        console.error("Invalid targetName in updateOffTargetData:", targetName);
        return;
      }

      if (!state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName] = {
          offTargets: {},
          offTargetFiles: {}
        };
      }

      if (!state.targetOffTargetData[targetName].offTargets[offTargetName]) {
        state.targetOffTargetData[targetName].offTargets[offTargetName] = {
          name: offTargetName,
          chain: null,
          ligandId: null,
          ligand_id: null,
          proteinSeq: null,
          seq: null,
          geneID: null,
          file: null,
          coordinates: null,
          userChoiceLocationXYZ: null,
          dockingBoxTab: null,
        };
      }

      state.targetOffTargetData[targetName].offTargets[offTargetName] = {
        ...state.targetOffTargetData[targetName].offTargets[offTargetName],
        ...updates,
      };
    },

    removeOffTarget: (
      state,
      action: PayloadAction<{ targetName: string; offTargetName: string }>
    ) => {
      const { targetName, offTargetName } = action.payload;

      if (state.targetOffTargetData[targetName]) {
        delete state.targetOffTargetData[targetName].offTargets[offTargetName];
        delete state.targetOffTargetData[targetName].offTargetFiles[offTargetName];
      }
    },

    setOffTargetFile: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        file: File | null;
      }>
    ) => {
      const { targetName, offTargetName, file } = action.payload;

      if (!targetName || targetName.trim() === "") {
        console.error(" Invalid targetName in setOffTargetFile:", targetName);
        return;
      }

      if (!state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName] = {
          offTargets: {},
          offTargetFiles: {},
        };
      }

      if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
          file: null,
          file_type: null,
          file_info: null,
          upload_status: "idle",
          error_message: null,
          chains: [],
          ligands: [],
          selectedChain: null,
          selectedLigand: null,
        };
      }

      state.targetOffTargetData[targetName].offTargetFiles[offTargetName].file = file;

      // 사용자가 off-target 파일을 직접 업로드하면 sample data 플래그 해제.
      // (target 파일 업로드 시 setUserTargetFile과 동일한 처리)
      // 이 리셋이 없으면, 샘플 호출 후 off-target을 업로드해도 sample_use가
      // true로 남아 file-callback이 sample_use:true로 호출되어 400이 발생한다.
      if (file) {
        state.isSampleData = false;
        state.sample_use = false;
      }
    },

    setOffTargetFileType: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        fileType: string | null;
      }>
    ) => {
      const { targetName, offTargetName, fileType } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName].file_type = fileType;
      }
    },

    setOffTargetFileInfo: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        fileInfo: FileInfo | null;
      }>
    ) => {
      const { targetName, offTargetName, fileInfo } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName].file_info = fileInfo;
      }
    },
    // kinase_userParameterSlice.ts의 reducers에 추가

setOffTargetPdbContent: (
  state,
  action: PayloadAction<{
    targetName: string;
    offTargetName: string;
    pdbContent: string;
  }>
) => {
  const { targetName, offTargetName, pdbContent } = action.payload;

  if (!state.targetOffTargetData[targetName]) {
    state.targetOffTargetData[targetName] = {
      offTargets: {},
      offTargetFiles: {}
    };
  }

  if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
    state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
      file: null,
      file_type: null,
      file_info: null,
      upload_status: 'idle',
      error_message: null,
      chains: [],
      ligands: [],
      selectedChain: null,
      selectedLigand: null,
      pdbContent: null
    };
  }

  state.targetOffTargetData[targetName].offTargetFiles[offTargetName].pdbContent = pdbContent;
},

    setOffTargetChainLigandData: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        chains: Chain[];
        ligands: Ligand[];
      }>
    ) => {
      const { targetName, offTargetName, chains, ligands } = action.payload;

      if (!targetName || targetName.trim() === "") {
        console.error(" Invalid targetName in setOffTargetChainLigandData:", targetName);
        return;
      }

      if (!state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName] = {
          offTargets: {},
          offTargetFiles: {},
        };
      }

      if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
          file: null,
          file_type: null,
          file_info: null,
          upload_status: "idle",
          error_message: null,
          chains: [],
          ligands: [],
          selectedChain: null,
          selectedLigand: null,
        };
      }

      state.targetOffTargetData[targetName].offTargetFiles[offTargetName].chains = chains;
      state.targetOffTargetData[targetName].offTargetFiles[offTargetName].ligands = ligands;
    },

    setOffTargetSelectedChain: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        chainId: string;
      }>
    ) => {
      const { targetName, offTargetName, chainId } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName].selectedChain = chainId;
      }
    },

    setOffTargetSelectedLigand: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        ligandId: string;
        ligandFullName: string;
      }>
    ) => {
      const { targetName, offTargetName, ligandId, ligandFullName } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName].selectedLigand = ligandId;
      }

      if (state.targetOffTargetData[targetName]?.offTargets[offTargetName]) {
        state.targetOffTargetData[targetName].offTargets[offTargetName].ligandId = ligandFullName;
      }
    },
    
    setOffTargetUploadStatus: (
      state,
      action: PayloadAction<{
        targetName: string;
        offTargetName: string;
        status: "idle" | "uploading" | "success" | "error";
        errorMessage?: string | null;
      }>
    ) => {
      const { targetName, offTargetName, status, errorMessage } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName].upload_status = status;
        if (errorMessage !== undefined) {
          state.targetOffTargetData[targetName].offTargetFiles[offTargetName].error_message = errorMessage;
        }
      }
    },

// 기존 clearOffTargetsForTarget를 수정
clearOffTargetsForTarget: (state, action: PayloadAction<string>) => {
  const targetName = action.payload;
  
  // targetOffTargetData에서 해당 타겟 전체 삭제
  if (state.targetOffTargetData[targetName]) {
    delete state.targetOffTargetData[targetName];
  }
},

    clearOffTargetFilesForTarget: (state, action: PayloadAction<string>) => {
      const targetName = action.payload;

      if (state.targetOffTargetData[targetName]) {
        state.targetOffTargetData[targetName].offTargetFiles = {};
      }
    },

    clearAllTargetOffTargetData: (state) => {
      state.targetOffTargetData = {};
    },

    clearOffTargetFile: (
      state,
      action: PayloadAction<{ targetName: string; offTargetName: string }>
    ) => {
      const { targetName, offTargetName } = action.payload;

      if (state.targetOffTargetData[targetName]?.offTargetFiles[offTargetName]) {
        state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
          file: null,
          file_type: null,
          file_info: null,
          upload_status: "idle",
          error_message: null,
          chains: [],
          ligands: [],
          selectedChain: null,
          selectedLigand: null,
        };
      }
    },


    resetTargetPdbResponse: (state) => {
      state.userUploadTargetPdbResponse = null;
      state.isTargetLoading = false;
      state.TargetPdbResponse = null;
    },

    clearAssayFileData: (state) => {
      clearAssayState(state);
    },
    clearWorkflowError: (state) => {
      state.message = "";
      state.status = null;
      state.error = null;
    },
    setShowNextRoundAssayModal: (state, action: PayloadAction<boolean>) => {
      state.showNextRoundAssayModal = action.payload;
    },
    setUserTargetLigandFullName: (state, action: PayloadAction<string>) => {
      state.setFullNameTargetLigandId = action.payload;
    },
    resetAssayFileInfo: (state) => {
      state.assay_file = null;
      state.assay_file_type = null;
      state.assay_file_info = null;
      state.userHaveAssayFile = false;
      state.assay_id = null;
      state.gt_label = null;
    },
    resetForSampleData: (state) => {
      state.target_file_info = null;
      state.assay_file_info = null;
      state.target_file = null;
      state.assay_file = null;
      state.userUploadTargetPdbFileInfo = null;
    
      state.filter_druglikeness = {
        drug_filter: {},
        min_hba_hbd_sum: null,
        qeppi_threshold: null,
        sa_threshold: null,
      };
      state.filter_kinase = {
        apply_bioactivity_filter: false,
        bioactivity_types: ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
        exclude_metal: false,
        apply_structural_filter: false,
        structural_filters: {
          "MW": [null, null],
          "HBA": [null, null],
          "HBD": [null, null],
          "NUM_AROMRINGS": [null, null]
        }
      };
      state.filter_adme_threshold = null;
      state.filter_plip = { plip: {} };
      state.hasPlipData = false;
      state.selected_plip_residues = [];
    
      state.protein_name = null;
      state.protein_seq = null;
      state.target_chain = null;
      state.target_ligand_id = null;
      state.chain = null;
      state.ligand_id = null;
      // state.locationXYZ = null;
    
      state.assay_id = null;
      state.gt_label = null;
      state.assay_info = { ...emptyAssayInfo };
    
      state.molecularNumber = null;
    
      state.targetOffTargetData = {};
      state.userChoiceTargetName = null;
      state.userChoiceOffTargetName = null;
      state.userChoiceKinaseTarget = "";

      state.availableChains = [];
      state.availableLigands = [];
      state.isSampleData = false;
    },
    setUserUploadTargetPdbResponse: (state, action: PayloadAction<any>) => {
      state.userUploadTargetPdbResponse = action.payload;
    },
    setUserUploadTargetPdbName: (state, action: PayloadAction<any>) => {
      state.userUploadTargetPdbName = action.payload;
    },
    setUserUploadTargetPdbChain: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.userUploadTargetPdbChain = action.payload;
      // 동기화
      state.target_chain = action.payload;
      // state.chain = action.payload;
    },
    setUserUploadTargetPdbLigandId: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.userUploadTargetPdbLigandId = action.payload;
      // 동기화
      state.target_ligand_id = action.payload;
      // state.ligand_id = action.payload;
    },
    setUserUploadTargetPdbLigandFullName: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.userUploadTargetPdbLigandFullName = action.payload;
    },
    setUserUploadTargetPdbFileType: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.userUploadTargetPdbFileType = action.payload;
    },
    setUserUploadTargetPdbFileInfo: (
      state,
      action: PayloadAction<FileInfo | null>
    ) => {
      state.userUploadTargetPdbFileInfo = action.payload;
    },
    setUserUploadChoiceDockingBoxTab: (
      state,
      action: PayloadAction<string | null>
    ) => {
      state.userUploadChoiceDockingBoxTab = action.payload;

    },
    setUserUploadLocationXYZ: (state, action: PayloadAction<string | null>) => {
      state.userUploadLocationXYZ = action.payload;
      // 동기화
      // state.locationXYZ = action.payload;
    },
    
    setAvailableChains: (state, action: PayloadAction<Chain[]>) => {
      state.availableChains = action.payload;
    },
    setKinaseRound2EnabledLibraries: (state, action) => {
      state.round2EnabledLibraries = action.payload;
    },
    
    setAvailableLigands: (state, action: PayloadAction<Ligand[]>) => {
      state.availableLigands = action.payload;
    },
    
    // Assay Info 관련 액션들
    setAssayIdCol: (state, action: PayloadAction<string | null>) => {
      state.assay_info.assay_id_col = action.payload;
    },
    setSmilesCol: (state, action: PayloadAction<string | null>) => {
      state.assay_info.smiles_col = action.payload;
    },
    setActivityCol: (state, action: PayloadAction<string | null>) => {
      state.assay_info.activity_col = action.payload;
    },
    setActivityColRename: (state, action: PayloadAction<string | null>) => {
      state.userChoiceKinaseResultTaskName = action.payload;
    },
    setThresholdVal: (
      state,
      action: PayloadAction<string | number | null | undefined>
    ) => {
      const value = action.payload;
      state.assay_info.threshold_val =
        value === undefined || value === "" ? null : value;
    },
    setThresholdDirection: (
      state,
      action: PayloadAction<string | null | undefined>
    ) => {
      const value = action.payload;
      state.assay_info.threshold_direction =
        value === undefined || value === "" ? null : value;
    },
    setActiveVals: (state, action: PayloadAction<string>) => {
      state.assay_info.active_vals = action.payload;
    },
    setInactiveVals: (state, action: PayloadAction<string>) => {
      state.assay_info.inactive_vals = action.payload;
    },
    setAssayInfo: (
      state,
      action: PayloadAction<Partial<AssayInfo> & { threshold_val?: any; threshold_direction?: any }>
    ) => {
      const payload = { ...action.payload };
      if (payload.threshold_val === undefined || payload.threshold_val === "") {
        payload.threshold_val = null;
      }
      if (
        payload.threshold_direction === undefined ||
        payload.threshold_direction === ""
      ) {
        payload.threshold_direction = null;
      }
      state.assay_info = { ...state.assay_info, ...payload };
    },
    setUserChoiceKinaseTaskName : (state, action : PayloadAction<string>)=>{
      state.userChoiceKinaseResultTaskName = action.payload;
    },
    setRound2EnabledForLibrary: (state, action) => {
      const libraryName = action.payload;
      state.round2EnabledLibraries[libraryName] = true;
      if (state.library === libraryName) {
        state.round2Enabled = true;
      }
    },
    setApiError: (state, action: PayloadAction<ApiError | null>) => {
      state.apiError = action.payload;
    },
    clearApiError: (state) => {
      state.apiError = null;
    },
    setDockingSmiles : (state, action: PayloadAction<string | null>) => {
      state.docking_smiles = action.payload;
    },

  },

  extraReducers: (builder) => {
    builder
        // ==================== temporarySaveFilterCallbackAPI ====================
        .addCase(temporarySaveFilterCallbackAPI.pending, (state) => {
          state.isLoadedFromTemporarySave = false;
          state.error = null;
        })
        .addCase(temporarySaveFilterCallbackAPI.fulfilled, (state, action) => {
          state.isFilterCallbackLoading = false;
          state.isLoadedFromTemporarySave = true;
          state.error = null;
          const response = action.payload;
          
          // sample_use 처리
          if (response?.sample_use !== undefined) {
            state.sample_use = response.sample_use;
            state.isSampleData = response.sample_use;
          }
          
          // kinase_off_targets
          if (response?.kinase_off_targets) {
            state.kinase_off_targets = response.kinase_off_targets;
          }
          
          const restorableAssayInfo = getAssayFileInfoFromResponse(response);
          const hasRestorableAssayInfo = Boolean(restorableAssayInfo);

          if (!hasRestorableAssayInfo) {
            clearAssayState(state);
          }

          // assay_file_info
          if (hasRestorableAssayInfo) {
            state.assay_file_info = restorableAssayInfo;
          }
          
          // filter_adme_threshold
          if (response?.filter_adme_threshold !== undefined) {
            state.filter_adme_threshold = response.filter_adme_threshold;
          }

          if (hasRestorableAssayInfo && response?.assay_file_name) {
            state.assay_file_info = {
              ...state.assay_file_info,
              file_name: response.assay_file_name,
            };
          }

          if (hasRestorableAssayInfo && response?.assay_file_type) {
            state.assay_file_info = {
              ...state.assay_file_info,
              file_extension: String(response.assay_file_type).replace(/^\./, ""),
            };
          }
          
          // filter_druglikeness
          if (response?.filter_druglikeness) {
            const apiDruglikeness = response.filter_druglikeness;
            state.filter_druglikeness = {
              drug_filter: apiDruglikeness.drug_filter || {},
              min_hba_hbd_sum: apiDruglikeness.min_hba_hbd_sum ?? null,
              qeppi_threshold: apiDruglikeness.qeppi_threshold ?? null,
              sa_threshold: apiDruglikeness.sa_threshold ?? null,
            };
          }
          
          // filter_plip
          if (response?.filter_plip) {
            state.filter_plip = response.filter_plip;
            state.hasPlipData = true;
            
            const selectedResidues: string[] = [];
            if (response.filter_plip.plip) {
              Object.entries(response.filter_plip.plip).forEach(
                ([interactionType, params]) => {
                  if (
                    params &&
                    typeof params === "object" &&
                    "residue" in params &&
                    params.residue
                  ) {
                    selectedResidues.push(params.residue as string);
                  }
                }
              );
            }
            state.selected_plip_residues = selectedResidues;
          }
          
          // filter_kinase
          if (response?.filter_kinase !== undefined) {
            const incomingFilter = response.filter_kinase;
            state.filter_kinase = {
              apply_bioactivity_filter: incomingFilter.apply_bioactivity_filter ?? false,
              bioactivity_types: incomingFilter.bioactivity_types ?? ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
              exclude_metal: incomingFilter.exclude_metal ?? false,
              apply_structural_filter: incomingFilter.apply_structural_filter ?? false,
              structural_filters: {
                MW: incomingFilter.structural_filters?.MW ?? [null, null],
                HBA: incomingFilter.structural_filters?.HBA ?? [null, null],
                HBD: incomingFilter.structural_filters?.HBD ?? [null, null],
                NUM_AROMRINGS: incomingFilter.structural_filters?.NUM_AROMRINGS ?? [null, null]
              }
            };
          }
          
          // num_mol
          if (response?.num_mol !== undefined) {
            state.molecularNumber = response.num_mol;
          }
          
          // kinase_target
          if (response?.kinase_target) {
            state.userChoiceKinaseTarget = response.kinase_target;
            state.userChoiceTargetName = response.kinase_target;
          }
          
// assay_info
	if (hasRestorableAssayInfo && response?.assay_info) {
	  const assayInfo = response.assay_info;
	  state.assay_info = {
    assay_id_col: assayInfo.assay_id_col ?? null,
    smiles_col: assayInfo.smiles_col ?? null,
    activity_col: assayInfo.activity_col ?? null,
    activity_col_rename: response.kinase_target ?? null,  // kinase_target 사용
    threshold_val: assayInfo.threshold_val ?? null,
    threshold_direction: assayInfo.threshold_direction ?? null,
    active_vals: assayInfo.active_vals ?? "",
    inactive_vals: assayInfo.inactive_vals ?? ""
  };
}
          
          if (response?.kinase_info) {
            const targetName = response.kinase_target;
            
            if (!state.userChoiceTargetName && targetName) {
              state.userChoiceTargetName = targetName;
            }
            
            if (!state.userChoiceKinaseTarget && targetName) {
              state.userChoiceKinaseTarget = targetName;
            }
            
            // 구조 생성
            if (targetName && !state.targetOffTargetData[targetName]) {
              state.targetOffTargetData[targetName] = {
                offTargets: {},
                offTargetFiles: {}
              };
            }
            
            const kinaseEntries = Object.entries(response.kinase_info);
            
            kinaseEntries.forEach(([kinaseName, kinaseData]: [string, any]) => {
              const isTarget = kinaseName === targetName;
              
              // coordinates 처리
              let coordinatesStr = null;
              if (kinaseData.coordinates) {
                if (Array.isArray(kinaseData.coordinates)) {
                  coordinatesStr = kinaseData.coordinates.join(',');
                } else if (typeof kinaseData.coordinates === 'string') {
                  coordinatesStr = kinaseData.coordinates;
                }
              }
              
              const hasValidCoordinates = coordinatesStr && coordinatesStr.length > 0;
              const shouldUseChainLigand = !hasValidCoordinates;
              
              if (isTarget) {

                // ⭐ 정규화 함수 추가
  const normalizeCoordinates = (coords: any): string | null => {
    if (!coords) return null;
    if (Array.isArray(coords)) {
      return coords.join(',');  // [10, 20, 30] → "10,20,30"
    }
    if (typeof coords === 'string') {
      return coords.replace(/\s/g, ''); // 공백 제거
    }
    return null;
  };
  let coordinatesStr = normalizeCoordinates(kinaseData.coordinates);  // ⭐ 정규화
  
  const hasValidCoordinates = coordinatesStr && coordinatesStr.length > 0;
  const shouldUseChainLigand = !hasValidCoordinates;

                // ========== Target 정보 처리 ==========
                if (!state.targetOffTargetData[kinaseName]) {
                  state.targetOffTargetData[kinaseName] = {
                    offTargets: {},
                    offTargetFiles: {}
                  };
                }
                
                const targetFileInfo = getTargetFileInfoFromResponse(
                  response,
                  kinaseName,
                  kinaseData
                );

                syncSelectedTargetFileInfo(state, kinaseName, targetFileInfo);
                
const targetInfo: any = {
    proteinName: kinaseName,
    proteinSeq: kinaseData.seq,
    seq: kinaseData.seq,
    geneID: kinaseData.GeneID,
    file: kinaseData.file,
    coordinates: Array.isArray(kinaseData.coordinates) 
                    ? kinaseData.coordinates.join(',')
                    : (kinaseData.coordinates || null),
    chain: kinaseData.chain || null,
    ligandId: kinaseData.ligand_id || null,
    ligand_id: kinaseData.ligand_id || null,
    dockingBoxTab: shouldUseChainLigand ? 'chainLigand' : 'coordinates'
  };
                
                state.targetOffTargetData[kinaseName].targetInfo = targetInfo;
                state.targetOffTargetData[kinaseName].offTargets[kinaseName] = {
                  ...targetInfo,
                  name: kinaseName,
                  userChoiceLocationXYZ: coordinatesStr
                };
                
                // Docking 설정
                if (shouldUseChainLigand) {
                  if (kinaseData.chain) {
                    state.userUploadTargetPdbChain = kinaseData.chain;
                    state.target_chain = kinaseData.chain;
                    state.chain = kinaseData.chain;
                  }
                  if (kinaseData.ligand_id) {
                    state.userUploadTargetPdbLigandId = kinaseData.ligand_id;
                    state.userUploadTargetPdbLigandFullName = kinaseData.ligand_id;
                    state.target_ligand_id = kinaseData.ligand_id;
                    state.ligand_id = kinaseData.ligand_id;
                  }
                  state.userUploadLocationXYZ = null;
                  state.locationXYZ = null;
                  state.userUploadChoiceDockingBoxTab = null;
                  state.userChoiceDockingBoxTab = null;
                } else {
                  state.userUploadTargetPdbChain = null;
                  state.userUploadTargetPdbLigandId = null;
                  state.userUploadTargetPdbLigandFullName = null;
                  state.target_chain = null;
                  state.target_ligand_id = null;
                  state.chain = null;
                  state.ligand_id = null;
                  
                  state.userUploadLocationXYZ = coordinatesStr;
                  state.locationXYZ = coordinatesStr;
                  
                  state.userUploadChoiceDockingBoxTab = 'coordinates';
                  state.userChoiceDockingBoxTab = 'coordinates';
                }
                
                state.protein_name = kinaseName;
                state.protein_seq = kinaseData.seq;
                
              } else {
     
                if (targetName) {
                  if (!state.targetOffTargetData[targetName]) {
                    state.targetOffTargetData[targetName] = {
                      offTargets: {},
                      offTargetFiles: {}
                    };
                  }
                  
                  const offTargetData: any = {
                    name: kinaseName,
                    proteinName: kinaseName,
                    proteinSeq: kinaseData.seq,
                    seq: kinaseData.seq,
                    geneID: kinaseData.GeneID,
                    file: kinaseData.file,
                    coordinates: Array.isArray(kinaseData.coordinates) 
                    ? kinaseData.coordinates.join(',')  // "10,20,30"
                    : (kinaseData.coordinates || null),
                  // userChoiceLocationXYZ도 동일하게 처리
                  userChoiceLocationXYZ: Array.isArray(kinaseData.userChoiceLocationXYZ)
                    ? kinaseData.userChoiceLocationXYZ.join(',')
                    : (kinaseData.userChoiceLocationXYZ || null),
                  dockingBoxTab: shouldUseChainLigand ? 'chainLigand' : 'coordinates'
                  };
                  
                  if (shouldUseChainLigand) {
                    if (kinaseData.chain) {
                      offTargetData.chain = kinaseData.chain;
                    }
                    if (kinaseData.ligand_id) {
                      offTargetData.ligandId = kinaseData.ligand_id;
                      offTargetData.ligand_id = kinaseData.ligand_id;
                    }
                  }
                  
                  state.targetOffTargetData[targetName].offTargets[kinaseName] = offTargetData;
                  
                  // Off-target file_info 생성
                  if (kinaseData.file) {
                    const fileName = kinaseData.file.replace(/\.(pdb|cif)$/i, '');
                    const fileExt = kinaseData.file.match(/\.(pdb|cif)$/i)?.[1] || 'pdb';
                    const existingOffTargetFile =
                      state.targetOffTargetData[targetName].offTargetFiles[kinaseName];
                    
                    state.targetOffTargetData[targetName].offTargetFiles[kinaseName] = {
                      file: null,
                      file_type: null,
                      file_info: {
                        file_name: fileName,
                        file_extension: fileExt,
                        file_size: 0,
                      },
                      upload_status: 'idle',
                      error_message: null,
                      chains: existingOffTargetFile?.chains || [],
                      ligands: existingOffTargetFile?.ligands || [],
                      selectedChain: shouldUseChainLigand
                        ? (kinaseData.chain || existingOffTargetFile?.selectedChain || null)
                        : null,
                      selectedLigand: shouldUseChainLigand
                        ? (kinaseData.ligand_id || existingOffTargetFile?.selectedLigand || null)
                        : null,
                      pdbContent: existingOffTargetFile?.pdbContent || null
                    };
                  }
                }
              }
            });
          }
          
          // kinase_off_targets를 배열로 변환 (문자열인 경우)
          if (response?.kinase_off_targets) {
            if (typeof response.kinase_off_targets === 'string') {
              state.kinase_off_targets = [response.kinase_off_targets];
              state.userChoiceKinaseOffTargetList = response.kinase_off_targets;
            } else if (Array.isArray(response.kinase_off_targets)) {
              state.kinase_off_targets = response.kinase_off_targets;
              state.userChoiceKinaseOffTargetList = response.kinase_off_targets.join(',');
            }
          }
          // kinase_target_info 독립 처리
          if (response?.kinase_target_info && !response?.kinase_info) {
            getTargetFileInfoEntries(
              response.kinase_target_info,
              response.kinase_target
            ).forEach(([targetName, fileInfo]: [string, any]) => {
              if (!state.targetOffTargetData[targetName]) {
                state.targetOffTargetData[targetName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
              syncSelectedTargetFileInfo(state, targetName, fileInfo);
              if (!state.userChoiceTargetName) {
                state.userChoiceTargetName = targetName;
              }
              if (!state.userChoiceKinaseTarget) {
                state.userChoiceKinaseTarget = targetName;
              }
            });
          }
          
          // target_file_info 독립 처리
          if (response?.target_file_info && !response?.kinase_info) {
            const targetEntries = getTargetFileInfoEntries(
              response.target_file_info,
              response.kinase_target
            );
            const targetNames = targetEntries.map(([targetName]) => targetName);
            const firstTargetName = targetNames[0];
            
            if (!state.userChoiceTargetName && firstTargetName) {
              state.userChoiceTargetName = firstTargetName;
            }
            
            if (!state.userChoiceKinaseTarget && firstTargetName) {
              state.userChoiceKinaseTarget = firstTargetName;
            }
            
            targetEntries.forEach(([targetName, fileInfo]: [string, any]) => {
              if (!state.targetOffTargetData[targetName]) {
                state.targetOffTargetData[targetName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
              
              const isInKinaseInfo = response?.kinase_info?.[targetName];
              
              if (!isInKinaseInfo) {
                if (targetName === state.userChoiceTargetName || targetName === state.userChoiceKinaseTarget) {
                  state.userUploadTargetPdbFileInfo = fileInfo;
                  state.userUploadTargetPdbName = targetName;
                  state.target_file_info = fileInfo;
                }
              }
              
              if (targetName === state.userChoiceTargetName || targetName === state.userChoiceKinaseTarget) {
                state.target_file_info = fileInfo;
              }
            });
          }
          
          // off_target_file_info 독립 처리
          if (response?.off_target_file_info && !response?.kinase_info) {
            const targetName = state.userChoiceTargetName;
            
            if (targetName) {
              if (!state.targetOffTargetData[targetName]) {
                state.targetOffTargetData[targetName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
              
              Object.entries(response.off_target_file_info).forEach(([offTargetName, fileInfo]: [string, any]) => {
                if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
                  state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
                    file: null,
                    file_type: null,
                    file_info: fileInfo,
                    upload_status: 'idle',
                    error_message: null,
                    chains: [],
                    ligands: [],
                    selectedChain: null,
                    selectedLigand: null
                  };
                } else {
                  state.targetOffTargetData[targetName].offTargetFiles[offTargetName].file_info = fileInfo;
                }
              });
            }
          }
        })
        .addCase(temporarySaveFilterCallbackAPI.rejected, (state, action) => {
          state.isFilterCallbackLoading = false;
          state.error = action.payload as string;
          
          // 에러 상세 정보 저장
          const error = action.payload as any;
          state.apiError = {
            status: error?.status || error?.response?.status,
            code: error?.code || error?.response?.data?.code,
            message: error?.message || error?.response?.data?.message || 'An error occurred',
            details: error?.response?.data
          };
        })
        
        // ==================== temporarySaveFileCallBackAPI ====================
        .addCase(temporarySaveFileCallBackAPI.pending, (state) => {
          state.isFileCallbackLoading = true;
          state.status = null;
          state.message = "";
        })
        .addCase(temporarySaveFileCallBackAPI.fulfilled, (state, action) => {
          state.isFileCallbackLoading = false;
          state.status = action.payload.status;
          
          const requestData = action.meta.arg;
          const responseData = action.payload.data;
          
          // sample_use 상태 업데이트
          if (action.payload.sample_use !== undefined) {
            state.sample_use = action.payload.sample_use;
            state.isSampleData = action.payload.sample_use;
          }
          
          const fileType = requestData.file_type;
          const targetName = requestData.target_name;
          const offTargetName = requestData.off_target_name;
          
          // Target PDB 파일인 경우
          if (fileType === 'target' && targetName) {
            state.TargetPdbResponse = responseData;
            
            if (state.userChoiceTargetName === targetName) {
              state.userUploadTargetPdbResponse = responseData;
            }
          }
          // Off-target PDB 파일인 경우  
          else if (fileType === 'off-target' && offTargetName && targetName) {
            const currentTargetName = targetName || state.userChoiceTargetName;
            
            if (currentTargetName) {
              if (!state.targetOffTargetData[currentTargetName]) {
                state.targetOffTargetData[currentTargetName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
            }
          }
          // Assay CSV 파일인 경우
          else if (fileType === 'assay') {
            if (isEmptyCallbackData(responseData)) {
              clearAssayState(state);
            } else {
              state.AssayCsvResponse = responseData;
              state.isAssayLoading = false;
            }
          }
          // 기타
          else {
            state.response = responseData;
          }
        })
        .addCase(temporarySaveFileCallBackAPI.rejected, (state, action) => {
          state.isFileCallbackLoading = false;
          
          const errorPayload = action.payload as any;
          const requestData = action.meta.arg;
          
          console.error('temporarySaveFileCallBackAPI Error:', {
            requestData,
            errorPayload
          });
          
          if (typeof errorPayload === "object" && errorPayload !== null) {
            state.message = errorPayload.error?.message || "An error occurred";
            state.status = errorPayload.error?.response?.status || null;
          } else {
            state.message = (errorPayload as string) || "An error occurred";
          }
        })
    

    .addCase(workFlowFilterCallbackAPI.pending, (state) => {
      state.isFilterCallbackLoading = true;
      state.error = null;
      
      // 새로운 데이터를 받기 전에 기존 coordinates 초기화
      const targetName = state.userChoiceKinaseTarget || state.userChoiceTargetName;
      if (targetName && state.targetOffTargetData[targetName]) {
        Object.keys(state.targetOffTargetData[targetName].offTargets).forEach(offTargetName => {
          if (state.targetOffTargetData[targetName].offTargets[offTargetName]) {
            // coordinates만 null로 초기화 (다른 데이터는 유지)
            state.targetOffTargetData[targetName].offTargets[offTargetName].coordinates = null;
            state.targetOffTargetData[targetName].offTargets[offTargetName].userChoiceLocationXYZ = null;
          }
        });
      }
    })
      .addCase(workFlowFilterCallbackAPI.fulfilled, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.isLoadedFromTemporarySave = false;
        state.error = null;
        const response = action.payload;
        const isSampleUse = action.meta.arg?.sample_use === true;
        const shouldRestoreAssayInfo = action.meta.arg?.restore_assay_info !== false;
        
        if (isSampleUse) {
          state.sample_use = true;
          state.isSampleData = true;
        }
      
        if (response?.sample_use !== undefined) {
          state.sample_use = response.sample_use;
          state.isSampleData = response.sample_use;
        }
        
        if (isSampleUse) {
          state.isSampleData = true;
        }
      
        // Sample 사용 시 필터 및 파일 정보 초기화
        if (isSampleUse) {
          state.target_file_info = null;
          state.assay_file_info = null;
          state.userUploadTargetPdbFileInfo = null;
      
          state.filter_druglikeness = {
            drug_filter: {},
            min_hba_hbd_sum: null,
            qeppi_threshold: null,
            sa_threshold: null,
          };
          
          state.filter_plip = { plip: {} };
          state.hasPlipData = false;
          state.filter_adme_threshold = null;
          state.molecularNumber = null;
          state.selected_plip_residues = [];
          
          state.availableChains = [];
          state.availableLigands = [];
          
          if (!response?.kinase_info) {
            state.targetOffTargetData = {};
            state.userChoiceTargetName = null;
            state.userChoiceOffTargetName = null;
          }
        }
      
        if(response?.sample_use !== undefined){
          state.sample_use = response.sample_use;
        }
      
        if(response?.kinase_off_targets){
          state.kinase_off_targets = response.kinase_off_targets;
        }
      
        const restorableAssayInfo = getAssayFileInfoFromResponse(response);
        const hasRestorableAssayInfo = Boolean(shouldRestoreAssayInfo && restorableAssayInfo);

        if (hasRestorableAssayInfo) {
          state.assay_file_info = restorableAssayInfo;
        } else if (shouldRestoreAssayInfo) {
          clearAssayState(state);
        }
      
        if (response?.filter_adme_threshold !== undefined) {
          state.filter_adme_threshold = response.filter_adme_threshold;
        }
      
        if (response?.filter_druglikeness) {
          const apiDruglikeness = response.filter_druglikeness;
          
          state.filter_druglikeness = {
            drug_filter: apiDruglikeness.drug_filter || {},
            min_hba_hbd_sum: apiDruglikeness.min_hba_hbd_sum ?? null,
            qeppi_threshold: apiDruglikeness.qeppi_threshold ?? null,
            sa_threshold: apiDruglikeness.sa_threshold ?? null,
          };
        }
      
        if (response?.filter_plip) {
          state.filter_plip = response.filter_plip;
          state.hasPlipData = true;
      
          const selectedResidues: string[] = [];
          if (response.filter_plip.plip) {
            Object.entries(response.filter_plip.plip).forEach(
              ([interactionType, params]) => {
                if (
                  params &&
                  typeof params === "object" &&
                  "residue" in params &&
                  params.residue
                ) {
                  selectedResidues.push(params.residue as string);
                }
              }
            );
          }
          state.selected_plip_residues = selectedResidues;
        }
      
        if (response?.filter_kinase !== undefined) {
          const incomingFilter = response.filter_kinase;
          
          state.filter_kinase = {
            apply_bioactivity_filter: 
              incomingFilter.apply_bioactivity_filter ?? false,
            
            bioactivity_types: 
              incomingFilter.bioactivity_types ?? ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
            
            exclude_metal: 
              incomingFilter.exclude_metal ?? false,
            
            apply_structural_filter: 
              incomingFilter.apply_structural_filter ?? false,
            
            structural_filters: {
              MW: incomingFilter.structural_filters?.MW ?? [null, null],
              HBA: incomingFilter.structural_filters?.HBA ?? [null, null],
              HBD: incomingFilter.structural_filters?.HBD ?? [null, null],
              NUM_AROMRINGS: incomingFilter.structural_filters?.NUM_AROMRINGS ?? [null, null]
            }
          };
        } else if (isSampleUse) {
          state.filter_kinase = {
            apply_bioactivity_filter: false,
            bioactivity_types: ['IC50', 'Kd', 'Ki', 'GI50', 'EC50', 'AC50'],
            exclude_metal: false,
            apply_structural_filter: false,
            structural_filters: {
              MW: [null, null],
              HBA: [null, null],
              HBD: [null, null],
              NUM_AROMRINGS: [null, null]
            }
          };
        }
      
        if (response?.num_mol !== undefined) {
          state.molecularNumber = response.num_mol;
        }
      
        if (response?.kinase_target) {
          state.userChoiceKinaseTarget = response.kinase_target;
          state.userChoiceTargetName = response.kinase_target;
        }
      
// assay_info
	if (hasRestorableAssayInfo && response?.assay_info) {
	  const assayInfo = response.assay_info;
	  state.assay_info = {
    assay_id_col: assayInfo.assay_id_col ?? null,
    smiles_col: assayInfo.smiles_col ?? null,
    activity_col: assayInfo.activity_col ?? null,
    activity_col_rename: response.kinase_target ?? null,  // kinase_target 사용
    threshold_val: assayInfo.threshold_val ?? null,
    threshold_direction: assayInfo.threshold_direction ?? null,
    active_vals: assayInfo.active_vals ?? "",
    inactive_vals: assayInfo.inactive_vals ?? ""
  };
}
      
        // ==========  kinase_info 처리 (배치 업데이트) ==========
        if (response?.kinase_info) {
          const targetName = response.kinase_target;
          
          if (!state.userChoiceTargetName && targetName) {
            state.userChoiceTargetName = targetName;
          }
          
          if (!state.userChoiceKinaseTarget && targetName) {
            state.userChoiceKinaseTarget = targetName;
          }
      
          // 1단계: 먼저 필요한 구조 생성
          if (targetName && !state.targetOffTargetData[targetName]) {
            state.targetOffTargetData[targetName] = {
              offTargets: {},
              offTargetFiles: {}
            };
          }
      
          //  2단계: 모든 kinase 데이터를 임시로 수집
          const kinaseEntries = Object.entries(response.kinase_info);
          
          kinaseEntries.forEach(([kinaseName, kinaseData]: [string, any]) => {
            const isTarget = kinaseName === targetName;
            
            const coordinatesStr = normalizeKinaseCoordinates(kinaseData.coordinates);
            const hasValidCoordinates = Boolean(coordinatesStr);
            const shouldUseChainLigand = !hasValidCoordinates;
            
            if (isTarget) {
              // Target 정보 처리
              if (!state.targetOffTargetData[kinaseName]) {
                state.targetOffTargetData[kinaseName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
              
              const targetInfo: any = {
                proteinName: kinaseName,
                proteinSeq: kinaseData.seq,
                seq: kinaseData.seq,
                geneID: kinaseData.GeneID,
                file: kinaseData.file,
                // 배열을 공백 없는 문자열로 변환
                coordinates: coordinatesStr,
                chain: kinaseData.chain || null,
                ligandId: kinaseData.ligand_id || null,
                ligand_id: kinaseData.ligand_id || null,
                dockingBoxTab: shouldUseChainLigand ? 'chainLigand' : 'coordinates'
              };
              
              // 한 번에 설정
              state.targetOffTargetData[kinaseName].targetInfo = targetInfo;
              state.targetOffTargetData[kinaseName].offTargets[kinaseName] = {
                ...targetInfo,
                name: kinaseName,
                userChoiceLocationXYZ: null
              };
            
              const targetFileInfo = getTargetFileInfoFromResponse(
                response,
                kinaseName,
                kinaseData
              );

              syncSelectedTargetFileInfo(state, kinaseName, targetFileInfo);
              syncSelectedTargetDockingInfo(state, kinaseData, shouldUseChainLigand);
              
              state.protein_name = kinaseName;
              state.protein_seq = kinaseData.seq;
              
            } else {
              // Off-target 정보 처리
              if (targetName) {
                if (!state.targetOffTargetData[targetName]) {
                  state.targetOffTargetData[targetName] = {
                    offTargets: {},
                    offTargetFiles: {}
                  };
                }
                
                const offTargetData: any = {
                  name: kinaseName,
                  proteinName: kinaseName,
                  proteinSeq: kinaseData.seq,
                  seq: kinaseData.seq,
                  geneID: kinaseData.GeneID,
                  file: kinaseData.file,
                  // 배열을 공백 없는 문자열로 변환
                  coordinates: coordinatesStr,
                  // userChoiceLocationXYZ도 동일하게 처리
                  userChoiceLocationXYZ: Array.isArray(kinaseData.userChoiceLocationXYZ)
                    ? kinaseData.userChoiceLocationXYZ.join(',')
                    : (kinaseData.userChoiceLocationXYZ || null),
                  dockingBoxTab: shouldUseChainLigand ? 'chainLigand' : 'coordinates'
                };
                
                if (shouldUseChainLigand) {
                  if (kinaseData.chain) {
                    offTargetData.chain = kinaseData.chain;
                  }
                  if (kinaseData.ligand_id) {
                    offTargetData.ligandId = kinaseData.ligand_id;
                    offTargetData.ligand_id = kinaseData.ligand_id;
                  }
                }
                
                state.targetOffTargetData[targetName].offTargets[kinaseName] = offTargetData;
      
                if (response.kinase_offtarget_info?.[kinaseName]) {
                  const existingOffTargetFile =
                    state.targetOffTargetData[targetName].offTargetFiles[kinaseName];

                  state.targetOffTargetData[targetName].offTargetFiles[kinaseName] = {
                    file: null,
                    file_type: null,
                    file_info: response.kinase_offtarget_info[kinaseName],
                    upload_status: 'idle',
                    error_message: null,
                    chains: existingOffTargetFile?.chains || [],
                    ligands: existingOffTargetFile?.ligands || [],
                    selectedChain: shouldUseChainLigand
                      ? (kinaseData.chain || existingOffTargetFile?.selectedChain || null)
                      : null,
                    selectedLigand: shouldUseChainLigand
                      ? (kinaseData.ligand_id || existingOffTargetFile?.selectedLigand || null)
                      : null,
                    pdbContent: existingOffTargetFile?.pdbContent || null
                  };
                }
                
                if (response.off_target_file_info?.[kinaseName]) {
                  const existingOffTargetFile =
                    state.targetOffTargetData[targetName].offTargetFiles[kinaseName];

                  state.targetOffTargetData[targetName].offTargetFiles[kinaseName] = {
                    file: null,
                    file_type: null,
                    file_info: response.off_target_file_info[kinaseName],
                    upload_status: 'idle',
                    error_message: null,
                    chains: existingOffTargetFile?.chains || [],
                    ligands: existingOffTargetFile?.ligands || [],
                    selectedChain: shouldUseChainLigand
                      ? (kinaseData.chain || existingOffTargetFile?.selectedChain || null)
                      : null,
                    selectedLigand: shouldUseChainLigand
                      ? (kinaseData.ligand_id || existingOffTargetFile?.selectedLigand || null)
                      : null,
                    pdbContent: existingOffTargetFile?.pdbContent || null
                  };
                }
              }
            }
          });
        }
      
        // kinase_target_info 독립 처리
        if (response?.kinase_target_info && !response?.kinase_info) {
          getTargetFileInfoEntries(
            response.kinase_target_info,
            response.kinase_target
          ).forEach(([targetName, fileInfo]: [string, any]) => {
            if (!state.targetOffTargetData[targetName]) {
              state.targetOffTargetData[targetName] = {
                offTargets: {},
                offTargetFiles: {}
              };
            }
            
            syncSelectedTargetFileInfo(state, targetName, fileInfo);
            
            if (!state.userChoiceTargetName) {
              state.userChoiceTargetName = targetName;
            }
            if (!state.userChoiceKinaseTarget) {
              state.userChoiceKinaseTarget = targetName;
            }
          });
        }
      
        // target_file_info 독립 처리
        if (response?.target_file_info && !response?.kinase_info) {
          const targetEntries = getTargetFileInfoEntries(
            response.target_file_info,
            response.kinase_target
          );
          const targetNames = targetEntries.map(([targetName]) => targetName);
          const firstTargetName = targetNames[0];
          
          if (!state.userChoiceTargetName && firstTargetName) {
            state.userChoiceTargetName = firstTargetName;
          }
          
          if (!state.userChoiceKinaseTarget && firstTargetName) {
            state.userChoiceKinaseTarget = firstTargetName;
          }
          
          targetEntries.forEach(([targetName, fileInfo]: [string, any]) => {
            if (!state.targetOffTargetData[targetName]) {
              state.targetOffTargetData[targetName] = {
                offTargets: {},
                offTargetFiles: {}
              };
            }
            
            const isInKinaseInfo = response?.kinase_info?.[targetName];
            
            if (!isInKinaseInfo) {
              if (targetName === state.userChoiceTargetName || targetName === state.userChoiceKinaseTarget) {
                state.userUploadTargetPdbFileInfo = fileInfo;
                state.userUploadTargetPdbName = targetName;
                state.target_file_info = fileInfo;
              }
            }
            
            if (targetName === state.userChoiceTargetName || targetName === state.userChoiceKinaseTarget) {
              state.target_file_info = fileInfo;
            }
          });
        }
      
        // off_target_file_info 독립 처리
        if (response?.off_target_file_info && !response?.kinase_info) {
          const targetName = state.userChoiceTargetName;
          
          if (targetName) {
            if (!state.targetOffTargetData[targetName]) {
              state.targetOffTargetData[targetName] = {
                offTargets: {},
                offTargetFiles: {}
              };
            }
      
            Object.entries(response.off_target_file_info).forEach(([offTargetName, fileInfo]: [string, any]) => {
              if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
                state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
                  file: null,
                  file_type: null,
                  file_info: fileInfo,
                  upload_status: 'idle',
                  error_message: null,
                  chains: [],
                  ligands: [],
                  selectedChain: null,
                  selectedLigand: null
                };
              } else {
                state.targetOffTargetData[targetName].offTargetFiles[offTargetName].file_info = fileInfo;
              }
            });
          }
        }
      })
      .addCase(workFlowFilterCallbackAPI.rejected, (state, action) => {
        state.isFilterCallbackLoading = false;
        state.error = action.payload as string;
      })

      .addCase(WorkFlowFileCallBackAPI.pending, (state, action) => {
        state.isFileCallbackLoading = true;
        state.status = null;
        state.message = "";

        const { saveToTargetPdbSave, saveToMimicPdbSave, saveToAssayCsvSave } =
          action.meta.arg;

        if (saveToTargetPdbSave) {
          state.isTargetLoading = true;
        }
        if (saveToMimicPdbSave) {
          state.isMimicLoading = true;
        }
        if (saveToAssayCsvSave) {
          state.isAssayLoading = true;
        }
      })
      // kinase_userParameterSlice.ts의 extraReducers


      .addCase(WorkFlowFileCallBackAPI.fulfilled, (state, action) => {
        state.isFileCallbackLoading = false;
        state.status = action.payload.status;
        
        const requestData = action.meta.arg;
        const responseData = action.payload.data;
      
        if (action.payload.sample_use !== undefined) {
          state.sample_use = action.payload.sample_use;
          state.isSampleData = action.payload.sample_use;
        }
      
        // Target PDB Response
        if (action.payload.saveToTargetPdbResponse) {
          const isSampleUse = requestData.sample_use === true;
          const fileType = requestData.file_type;
          const targetName = requestData.target_name;
      
          if (isSampleUse || fileType === 'target') {
            state.TargetPdbResponse = responseData;
            
            //  Target의 PDB도 저장
            if (targetName && typeof responseData === 'string') {
              if (!state.targetOffTargetData[targetName]) {
                state.targetOffTargetData[targetName] = {
                  offTargets: {},
                  offTargetFiles: {}
                };
              }
              
              if (!state.targetOffTargetData[targetName].offTargetFiles[targetName]) {
                state.targetOffTargetData[targetName].offTargetFiles[targetName] = {
                  file: null,
                  file_type: null,
                  file_info: null,
                  upload_status: 'idle',
                  error_message: null,
                  chains: [],
                  ligands: [],
                  selectedChain: null,
                  selectedLigand: null,
                  pdbContent: null
                };
              }
              
              state.targetOffTargetData[targetName].offTargetFiles[targetName].pdbContent = responseData;
            }
            
            if (targetName && state.userChoiceTargetName === targetName) {
              state.userUploadTargetPdbResponse = responseData;
            }
          }
          
          state.isTargetLoading = false;
          return;
        }
      
        // Off-target PDB Response
        if (action.payload.saveToMimicPdbResponse) {
          const fileType = requestData.file_type;
          const offTargetName = requestData.off_target_name;
          const targetName = requestData.target_name || state.userChoiceTargetName;
      
          if (fileType === 'off-target' && offTargetName && targetName && typeof responseData === 'string') {
            if (!state.targetOffTargetData[targetName]) {
              state.targetOffTargetData[targetName] = {
                offTargets: {},
                offTargetFiles: {}
              };
            }
      
            if (!state.targetOffTargetData[targetName].offTargets[offTargetName]) {
              state.targetOffTargetData[targetName].offTargets[offTargetName] = {
                name: offTargetName,
                chain: null,
                ligandId: null,
                ligand_id: null,
                proteinSeq: null,
                file: null,
                coordinates: null,
                userChoiceLocationXYZ: null,
                dockingBoxTab: null,
              };
            }
      
            if (!state.targetOffTargetData[targetName].offTargetFiles[offTargetName]) {
              state.targetOffTargetData[targetName].offTargetFiles[offTargetName] = {
                file: null,
                file_type: fileType,
                file_info: null,
                upload_status: 'success',
                error_message: null,
                chains: [],
                ligands: [],
                selectedChain: null,
                selectedLigand: null,
                pdbContent: null
              };
            }
            
            // Off-target PDB 저장
            state.targetOffTargetData[targetName].offTargetFiles[offTargetName].pdbContent = responseData;
          }
          
          state.isMimicLoading = false;
          return;
        }

        if (action.payload.saveToAssayCsvResponse || requestData.file_type === 'assay') {
          if (isEmptyCallbackData(responseData)) {
            state.AssayCsvResponse = null;
            state.isAssayLoading = false;
          } else {
            state.AssayCsvResponse = responseData;
            state.isAssayLoading = false;
          }
          return;
        }
      
        state.response = responseData;
      })
      .addCase(WorkFlowFileCallBackAPI.rejected, (state, action) => {
        state.isFileCallbackLoading = false;

        const errorPayload = action.payload as any;
        const requestData = action.meta.arg;

        console.error('WorkFlowFileCallBackAPI Error:', {
          requestData,
          errorPayload
        });

        if (typeof errorPayload === "object" && errorPayload !== null) {
          state.message = errorPayload.error?.message || "An error occurred";
          state.status = errorPayload.error?.response?.status || null;
        } else {
          state.message = (errorPayload as string) || "An error occurred";
        }

        if (requestData.saveToTargetPdbSave) {
          state.isTargetLoading = false;
        }
        if (requestData.saveToMimicPdbSave) {
          state.isMimicLoading = false;
        }
        if (requestData.saveToAssayCsvSave) {
          state.isAssayLoading = false;
        }
        if (requestData.saveToAssayCsvSave || requestData.file_type === 'assay') {
          clearAssayState(state);
        }
      });
  },
});

export const {
  setUserChain,
  setUserLigandID,
  setUserFileUploadChecking,
  setLocationXYZ,
  setTargetFile,
  setTargetFileType,
  setAssayFileType,
  setAssayFile,
  setAssayId,
  setGtLabel,
  setDruglikeness,
  resetAllFileInfo,
  setBioactivityFilter,
  setPlip,
  clearPlipData,
  setAdme,
  setProteinName,
  setProteinSequence,
  setMolecularNumber,
  setTargetChainID,
  setTargetLigandID,
  setDrugReceptorChainID,
  setDrugChainLigandID,
  setAllResidueList,
  setSelectedPlipResidues,
  setUserChoiceStep,
  setUserChoiceDockingTab,
  resetAllParametersExceptLibrary,
  resetAllParameters,
  setUserChoiceTool,
  setUserInputWorkSpaceName,
  setUserChoiceKinaseTarget,
  setUserChoiceKinaseOffTargetList,
  setUserToolRunningStatus,
  resetAssayFileInfo,
  setUserHaveAssayFile,
  setTargetFileInfo,
  setAssayFileInfo,
  setPdbLigandResponse,
  setTargetPdbResponse,
  setAssayCsvResponse,
  clearAssayCallbackState,
  resetWorkflowResult,
  resetWorkFlowFileCallback,
  resetTargetPdbResponse,
  clearAssayFileData,
  clearWorkflowError,
  resetForSampleData,
  setShowNextRoundAssayModal,
  setUserRunningStep,
  setAutoStepSave,
  setCardExpanded,
  setUserTargetLigandFullName,
  setTargetInfo,
  addOffTarget,
  removeOffTarget,
  setOffTargetFile,
  setOffTargetFileType,
  setOffTargetFileInfo,
  setOffTargetUploadStatus,
  clearOffTargetFilesForTarget,
  clearOffTargetsForTarget,
  clearAllTargetOffTargetData,
  clearOffTargetFile,
  setUserChoiceTargetName,
  setUserChoiceOffTargetName,
  setUserUploadTargetPdbResponse,
  setUserUploadTargetPdbName,
  setUserUploadTargetPdbChain,
  setUserUploadTargetPdbLigandId,
  setUserUploadTargetPdbLigandFullName,
  setUserTargetFile,
  setUserUploadTargetPdbFileType,
  setUserUploadTargetPdbFileInfo,
  setUserUploadChoiceDockingBoxTab,
  setUserUploadLocationXYZ,
  setOffTargetChainLigandData,
  setOffTargetSelectedChain,
  setOffTargetSelectedLigand,
  updateOffTargetData,
  setAssayIdCol,
  setSmilesCol,
  setActivityCol,
  setActivityColRename,
  setThresholdVal,
  setThresholdDirection,
  setActiveVals,
  setInactiveVals,
  setAssayInfo,
  setRound2EnabledForLibrary,
  setKinaseRound2EnabledLibraries,
  setAvailableChains,
  setAvailableLigands,
  setUserChoiceKinaseTaskName,
  setOffTargetPdbContent,
  setDockingSmiles
} = userKinaseParameterSlice.actions;

export default userKinaseParameterSlice.reducer;
